import classNames from 'classnames';
import {useState} from 'react';

import Icon from '../_icon';
import {isValidUrl} from '../../modules/url_utils';

const PageMonitor = ({monitor, onDeleteHit, onSaveHit, onCancelEditHit}, {appData}) => {
  const {id, url, type, title, highlights, lastChangeAt} = monitor;
  const [editing, setEditing] = useState(!url);
  const [value, setValue] = useState(url);
  const [invalid, setInvalid] = useState(false);
  const [dirty, setDirty] = useState(false);

  const isFullpageMonitor = type === 'page';
  const highlight = highlights && highlights.length ? highlights[0] : {};
  const {id: highlightId, crawlCount} = highlight;

  // no highlight Id =>  Monitor hasn't been saved yet
  // crawlCount === 0 => we don't have any hightlight for it yet
  // crawlCount === 1 => it is crawled just once, so there is no changes to display
  // lastChangeAt === null => hightlight hasn't changed since creation
  const highlightURL = (lastChangeAt && highlightId && Boolean(crawlCount) && crawlCount !== 1) ? `${appData.rootUrl}monitors?monitor=${id}` : undefined;

  const handleOnChange = e => {
    const newValue = e?.target?.value || '';

    setInvalid(false);
    setDirty(newValue !== url);
    setValue(newValue);
  };

  const handleOnClick = e => e && e.stopPropagation();

  const handleDeleteClicked = (shouldVerifyDelete = false) => {
    setEditing(false);
    onDeleteHit(monitor, shouldVerifyDelete);
  };

  const handleSaveClicked = () => {
    if(!isValidUrl(value)) {
      return setInvalid(true);
    }

    setEditing(false);
    setDirty(false);
    onSaveHit(monitor, value);
  };

  const handleCancelClicked = () => {
    setEditing(false);
    setValue(url);
    onCancelEditHit(monitor);
  };

  const handleKeyPress = event => {
    const e = event || window.event;

    if((e.which === 13) && dirty) {
      handleSaveClicked();
    }
  };

  return (
    <div
      className={classNames('page-monitor', {'page-monitor--editing': editing})}>
      {editing
        ? (
          <div className="page-monitor_url-container" data-tracking-id="monitors-url-container">
            <div className={classNames('page-monitor_url', {'page-monitor_url--editing': editing})}>
              <input
                name={name}
                data-testid="page-monitor-url-input"
                type="text"
                value={value || ''}
                onClick={handleOnClick}
                placeholder="e.g. https://companyname.com/about"
                onChange={handleOnChange}
                onKeyPress={handleKeyPress}
                autoFocus={true} />
              <button
                className={classNames('button button--small button--save', {'button--disabled': !dirty})}
                onClick={handleSaveClicked}
                disabled={!dirty}
                data-testid="page-monitor-url-save">
                save
              </button>
              <button
                className="button button--small button--cancel"
                onClick={url ? handleCancelClicked : () => handleDeleteClicked()}>
                {url ? 'cancel' : 'discard'}
              </button>
            </div>
            {invalid && (<div className="page-monitor_url-error" data-testid="url-whoops">
              Whoops! Please enter a valid URL.
            </div>)}
          </div>
        )
        : (
          <div className="page-monitor_readonly-container">
            <div className="page-monitor_row-left">
              <div className="page-monitor_icon-section">
                <div className="page-monitor_icon">
                  <Icon icon={isFullpageMonitor ? 'photo' : 'detail'} height="22" width="22" />
                </div>
              </div>
              <div className="page-monitor_text-section">
                {title && (<div className="page-monitor_title">
                  {title}
                </div>)}
                <div className={classNames('page-monitor_url', {'page-monitor_url--editing': editing})}>
                  <a href={url} target="_blank">{url}</a>
                </div>
              </div>
            </div>
            <div className="page-monitor_control-section">
              <a
                className={classNames('page-monitor_control page-monitor_control--active', {'page-monitor_control--disable': Boolean(!highlightURL)})}
                href={highlightURL}
                target="_blank"
                aria-label="track changes">
                <Icon icon="track-changes" height="22" width="22" />
              </a>
              <a
                className="page-monitor_control page-monitor_control--delete"
                href="#"
                onClick={() => handleDeleteClicked(true)}
                aria-label="delete monitor">
                <Icon icon="deleteX" height="22" width="22" />
              </a>
            </div>
          </div>
        )}
    </div>
  );
};

PageMonitor.propTypes = {
  monitor: PropTypes.object,
  onDeleteHit: PropTypes.func.isRequired,
  onSaveHit: PropTypes.func.isRequired,
  onCancelEditHit: PropTypes.func
};

PageMonitor.defaultProps = {
  monitor: {},
  onCancelEditHit() {}
};

PageMonitor.contextTypes = {
  appData: PropTypes.object.isRequired
};

export default PageMonitor;
